import React, { useEffect, useState } from "react";
import cx from "classnames";
import { SalesStatus } from "../../@types";
import { EmptyPieChart } from "./PieChart/Empty";
import { customColors, customTextColors } from "../Sales/invoice/Status/status";
import { formatMoney, useGetPaymentCurrencyList } from "../../utils";
import { PieChart, Pie, Tooltip } from "recharts";
import { useCompanyStore } from "../../state/useCompanyStore";
import InvoiceStatusEmptyIcon from "../../assets/icons/InvoiceStatusEmptyStatIcon";
import ChartLoader from "./Loading/ChartLoader";

interface PieData {
  value: number;
  fill: string;
}

interface PieChartProps {
  title?: string;
  isLoading?: boolean;
  data: Record<string, PieData> | any;
  toolTipText?: string;
  isFilter: boolean;
}
const InvoiceStatus: React.FC<PieChartProps> = ({
  title,
  data = {},
  isLoading,
  toolTipText,
  isFilter,
}) => {
  const [chartData, setChartData] = useState<any[]>([]);
  const companyProfile = useCompanyStore();
  const { userCurrencyCode } = useGetPaymentCurrencyList(
    companyProfile.company,
  );

  useEffect(() => {
    const transformedData = Object.keys(data)
      .filter((key) => data[key].fill && data[key].value !== null)
      .map((key) => ({
        name: key,
        fill: data[key].fill,
        value: data[key].value,
        amount: data[key].amount,
      }));
    setChartData(transformedData);
  }, [data]);

  const renderEmptyComponent = () => {
    return (
      <div className="pb-2 pt-0 bg-white rounded-lg w-full mb-10">
        <EmptyPieChart
          link={`/invoices/create`}
          icon={<InvoiceStatusEmptyIcon />}
          paragraph={`No data yet for cashflow`}
          title="Invoice status"
        />
      </div>
    );
  };
  const renderEmptyFilteredComponent = () => {
    return (
      <>
        <div className="py-4 px-5">
          <p className={`font-semibold text-[16px] text-[##000000] leading-6`}>
            {title}
          </p>
        </div>
        <hr className="border border-[#EAECF0] mt-1 w-full" />
        <div className=" pt-0 bg-white rounded-lg h-[285px] pb-10 flex justify-center items-center">
          <h1 className="text-medium text-[15px] text-[##000000] leading-6">
            No data available for this date range.
          </h1>
        </div>
      </>
    );
  };

  return (
    <>
      {isLoading ? (
        <ChartLoader />
      ) : (
        <>
          {chartData.length == 0 ? (
            <>
              {isFilter
                ? renderEmptyFilteredComponent()
                : renderEmptyComponent()}
            </>
          ) : (
            <div>
              {/* Invoice Subheading */}
              {chartData.length == 0 || (
                <>
                  <div className="py-4 px-5">
                    <p
                      className={`font-semibold text-[16px] text-[##000000] leading-6`}
                    >
                      {title}
                    </p>
                  </div>
                  <hr className="border border-[#EAECF0] mt-1 w-full" />
                </>
              )}

              <div
                className={`flex flex-col items-center justify-center px-6 pb-5 w-full h-full ${chartData.length == 0 && "mt-10"}`}
              >
                {chartData.length > 0 ? (
                  <PieChart width={220} height={200}>
                    <Pie
                      data={chartData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={60}
                      outerRadius={85}
                      paddingAngle={0}
                      style={{ outline: "none" }}
                    />
                    <Tooltip
                      formatter={(value: number, name: string, entry: any) =>
                        `${formatMoney(entry.payload.amount, userCurrencyCode.currency)}`
                      }
                      contentStyle={{
                        fontSize: "12px",
                        padding: "5px",
                        backgroundColor: "#FFFFFF",
                        border: "none",
                        borderRadius: "8px",
                      }}
                    />
                  </PieChart>
                ) : (
                  <EmptyPieChart
                    link={`/invoices/create`}
                    buttonText="Create an invoice"
                    paragraph={`Create your invoice to start recording payments`}
                  />
                )}

                <div className="flex flex-wrap gap-3 justify-center">
                  {chartData &&
                    chartData?.map((item, i) => {
                      const backgroundColor =
                        customColors[item.name as keyof typeof customColors];
                      const textColor =
                        customTextColors[
                          item.name as keyof typeof customTextColors
                        ];
                      const formattedLabel =
                        item.name.charAt(0).toUpperCase() +
                        item.name.slice(1).toLowerCase();

                      return (
                        <div
                          className={`flex flex-row gap-1 items-center justify-center`}
                          key={i}
                        >
                          <div
                            className={cx(`rounded-lg w-[8px] h-[8px]`)}
                            style={{ backgroundColor: textColor }}
                          ></div>

                          <p className="flex justify-between font-semibold leading-4 text-[14px] text-[#344054]">
                            {formattedLabel} <span> - {item.value} </span>{" "}
                            <span> % </span>{" "}
                          </p>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export { InvoiceStatus };
